import React from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Tag, Truck, Star } from 'lucide-react';
import SEO from '../components/SEO';

const ANNOUNCEMENTS = [
  {
    icon: Megaphone,
    tag: 'Launch',
    tagColor: 'bg-brand-teal/10 text-brand-teal',
    title: 'The Yaad Shop Is Now Open for Business',
    desc: "We're officially live! The Yaad Shop is now accepting wholesale account applications from African, Caribbean, Asian, Latin American, and ethnic stores across Canada. Browse our catalogue, apply for an account, and start ordering today.",
    date: 'February 2026',
  },
  {
    icon: Truck,
    tag: 'Shipping',
    tagColor: 'bg-brand-green-accent/10 text-brand-green-accent',
    title: 'Now Shipping Canada-Wide',
    desc: 'Great news for businesses outside the GTA — we now ship wholesale orders across Canada. Reliable packaging, competitive rates, and tracking on every order.',
    date: 'February 2026',
  },
  {
    icon: Tag,
    tag: 'Products',
    tagColor: 'bg-brand-gold/10 text-brand-gold',
    title: 'New Categories Coming Soon',
    desc: "We're expanding our product range with new multicultural essentials. Stay tuned for additions including beverages, seasonings, and more from communities around the world.",
    date: 'Coming Soon',
  },
  {
    icon: Star,
    tag: 'Promo',
    tagColor: 'bg-brand-coral/10 text-brand-coral',
    title: 'Early Wholesale Partners — Special Introductory Pricing',
    desc: 'Be among our first wholesale partners and lock in introductory pricing on our full catalogue. Apply for your account today to take advantage.',
    date: 'Limited Time',
  },
];

const NewsPromotions: React.FC = () => {
  return (
    <div className="bg-brand-warm">
      <SEO
        title="News & Promotions"
        description="Latest news, promotions, and wholesale deals from The Yaad Shop. Stay updated on new products and seasonal offers."
        path="/news"
      />
      {/* Page Header */}
      <section className="bg-brand-navy">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-14">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-teal-light mb-3">Stay Updated</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">News & Promotions</h1>
          <p className="text-base text-white/60 max-w-[560px] leading-relaxed">
            The latest updates, product announcements, and deals from The Yaad Shop.
          </p>
        </div>
      </section>

      {/* News Cards */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-10 py-20">
        <div className="max-w-3xl mx-auto space-y-6">
          {ANNOUNCEMENTS.map((item) => (
            <article
              key={item.title}
              className="bg-white rounded-2xl border border-gray-100 p-8 transition-all hover:shadow-lg hover:border-brand-teal"
            >
              <div className="flex items-start gap-5">
                <div className="w-[50px] h-[50px] rounded-xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-brand-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md ${item.tagColor}`}>
                      {item.tag}
                    </span>
                    <span className="text-[12px] text-brand-text-muted">{item.date}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg text-brand-navy mb-2 leading-snug">{item.title}</h3>
                  <p className="text-sm text-brand-text-muted leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Subscribe / CTA */}
      <section className="bg-brand-light-bg">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs font-bold uppercase tracking-[2px] text-brand-green mb-3">Don't Miss Out</div>
            <h2 className="font-display text-3xl font-extrabold text-brand-navy mb-3">Stay in the Loop</h2>
            <p className="text-base text-brand-text-muted mb-8 leading-relaxed">
              Apply for a wholesale account to receive updates on new products, promotions, and exclusive deals directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link
                to="/apply"
                className="bg-brand-teal text-white px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:bg-brand-teal-dark transition-all shadow-lg shadow-brand-teal/25 hover:-translate-y-0.5"
              >
                Apply for Wholesale Account
              </Link>
              <Link
                to="/shop"
                className="bg-white text-brand-navy border border-gray-200 px-8 py-3.5 font-semibold text-[15px] rounded-[10px] hover:border-brand-teal hover:text-brand-teal transition-all"
              >
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsPromotions;
